import { type Client } from "@/types";
import { getSupabaseClient } from "@/integrations/supabase/client";
import { toPostgrestIlikeValue } from "@/lib/postgrest";

type ClientRow = {
  id: string;
  organization_id: string;
  name: string;
  tipo: string;
  identification: string;
  email: string;
  phone?: { country_code?: string; number?: string } | null;
  address?: {
    province?: string;
    canton?: string;
    district?: string;
    other?: string;
  } | null;
};

function mapClientRow(row: ClientRow): Client {
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    tipo: row.tipo,
    identification: row.identification,
    email: row.email,
    phone: {
      country_code: row.phone?.country_code ?? '',
      number: row.phone?.number ?? '',
    },
    address: {
      province: row.address?.province ?? '',
      canton: row.address?.canton ?? '',
      district: row.address?.district ?? '',
      other: row.address?.other ?? '',
    },
  };
}

const clientsService = {
  async getClients(organizationId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) throw error;

    return (data ?? []).map(mapClientRow);
  },
  async getClientsPage(organizationId: string, opts: {
    page: number;
    pageSize?: number;
    search?: string;
  }) {
    const pageSize = opts.pageSize ?? 20;
    const from = (opts.page - 1) * pageSize;
    const to = from + pageSize - 1;
    const pattern = toPostgrestIlikeValue(opts.search ?? '');
    const supabase = getSupabaseClient();

    let query = supabase
      .from('clients')
      .select('*', { count: 'exact' })
      .eq('organization_id', organizationId)
      .order('name')
      .range(from, to);

    if (pattern) {
      query = query.or(
        [
          `name.ilike.${pattern}`,
          `email.ilike.${pattern}`,
          `identification.ilike.${pattern}`,
          `phone->>number.ilike.${pattern}`,
        ].join(',')
      );
    }
  
    const { data, error, count } = await query;
    if (error) throw error;
  
    return {
      clients: (data ?? []).map(mapClientRow),
      total: count ?? 0,
    };
  },
  async createClient(client: Omit<Client, 'id' | 'organizationId' | 'createdAt' | 'updatedAt'>, organizationId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('clients')
      .insert([{
        name: client.name,
        email: client.email,
        phone: {
          country_code: client.phone.country_code,
          number: client.phone.number,
        },
        address: {
          province: client.address.province,
          canton: client.address.canton,
          district: client.address.district,
          other: client.address.other,
        },
        organization_id: organizationId,
        tipo: client.tipo,
        identification: client.identification,
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateClient(client: Client) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('clients')
      .update({
        name: client.name,
        email: client.email || null,
        address: {
          province: client.address.province || null,
          canton: client.address.canton || null,
          district: client.address.district || null,
          other: client.address.other || null,
        },
        phone: {
          country_code: client.phone.country_code || null,
          number: client.phone.number || null,
        },
        tipo: client.tipo || null,
        identification: client.identification || null,
      })
      .eq('id', client.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
  async deleteClient(clientId: string) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId);

    if (error) throw error;
    return true;
  },
  async getClientById(clientId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single();

    if (error) throw error;
    return mapClientRow(data);
  },
  async getClientByIdentification(organizationId: string, identification: string) {
    const trimmed = identification.trim();
    if (!trimmed) return null;

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('identification', trimmed)
      .maybeSingle();

    if (error) throw error;
    return data ? mapClientRow(data) : null;
  },
};

export default clientsService;